import { useQuery } from '@tanstack/react-query';

import { ENDPOINTS, RadiologistStudyKeyImagesResponse, removeEmptyParams } from '@/api';
import { fetcher } from '@/api/fetcher';
import { useStudyGuard } from '@/shared/guard';
import { getMappedKeyImages } from '@/shared/util';
import { useKeyImagesStore } from '@/store';

export const KEY = {
  KEY_IMAGES_RETRIEVE: 'KEY_IMAGES_RETRIEVE',
} as const;

export const useKeyImagesRetrieveQuery = () => {
  const { studyId } = useStudyGuard();
  const { setIsKeyImagesLoading, setKeyImages, setShouldRefetchKeyImages } = useKeyImagesStore();

  const fetchKeyImagesManifest = async () => {
    const { data } = await fetcher.HI.get<RadiologistStudyKeyImagesResponse>(
      `${ENDPOINTS.HI.RADIOLOGIST.STUDY}${studyId}/ki-manifest`,
      { params: removeEmptyParams({ key_images: true, return_images: true, return_urls: true }) },
    );

    if (!data || !data.key_images) {
      throw new Error('Invalid key images response');
    }

    // FC-1171: adding num_instances bypass workaround given the unstable response from HI KI query, same apporach as Radform
    const isMatchingKeyImageLength =
      data.key_images.num_instances === Object.keys(data.key_images.imageKeys).length ||
      !data.key_images.num_instances;

    const isProcessingComplete =
      data.key_images.isProcessing === '0' &&
      data.key_images.isProcessed === true &&
      data.key_images.isAvailable === true &&
      data.key_images.processed &&
      Object.keys(data.key_images.processed).length > 0 &&
      isMatchingKeyImageLength;

    if (isProcessingComplete) {
      const mappedKeyImages = getMappedKeyImages(data);

      setKeyImages(mappedKeyImages);
      setIsKeyImagesLoading(false);
      setShouldRefetchKeyImages(false);

      return data;
    }

    throw new Error(
      `Key images not ready: Processing=${data.key_images.isProcessing}, Processed=${data.key_images.isProcessed}, Available=${data.key_images.isAvailable}`,
    );
  };

  const query = useQuery({
    enabled: false,
    queryFn: fetchKeyImagesManifest,
    queryKey: [KEY.KEY_IMAGES_RETRIEVE, studyId],
    retry: (failureCount) => {
      if (failureCount < 8) {
        setIsKeyImagesLoading(true);

        return true;
      }

      return false;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex + Math.random() * 1000, 30000),
    staleTime: 5 * 60 * 1000,
  });

  return { ...query, data: query.data, isLoading: query.isLoading, refetch: query.refetch };
};
